/**
 * Camera: world ↔ screen, pan, zoom, and framing.
 *
 * The galaxy is only 61 hexes, so the default view fits the whole board — a four-player
 * couch game where somebody has to scroll to see the board is a bad four-player couch
 * game. Zoom exists for inspecting a crowded system, and the camera nudges toward the
 * active cursor so the focused hex is never behind the HUD.
 */

import { HEX_SIZE, MAP_RADIUS } from '../game/constants';
import { corners, fromPixel, toPixel } from '../game/hex';
import type { Axial, HexKey, Point } from '../game/types';
import { parseKey } from '../game/hex';

export interface Viewport {
  /** CSS pixel size of the canvas. */
  width: number;
  height: number;
  dpr: number;
  /** Safe-area insets consumed by the HUD, so `frameBoard` can centre in what is left. */
  insets: { top: number; right: number; bottom: number; left: number };
}

export const MIN_ZOOM = 0.45;
export const MAX_ZOOM = 2.4;

export class Camera {
  /** World-space point at the centre of the visible area. */
  x = 0;
  y = 0;
  zoom = 1;

  /** Where the camera is easing toward. */
  private targetX = 0;
  private targetY = 0;
  private targetZoom = 1;

  /** Screen-space shake offset, driven by `shake()`. */
  private shakeAmount = 0;
  private shakePhase = 0;

  viewport: Viewport = {
    width: 1280,
    height: 720,
    dpr: 1,
    insets: { top: 0, right: 0, bottom: 0, left: 0 },
  };

  /* -------------------------------------------------------------- transforms */

  /** Centre of the region not covered by HUD chrome, in screen pixels. */
  get focusCenter(): Point {
    const { width, height, insets } = this.viewport;
    return {
      x: insets.left + (width - insets.left - insets.right) / 2,
      y: insets.top + (height - insets.top - insets.bottom) / 2,
    };
  }

  worldToScreen(point: Point): Point {
    const c = this.focusCenter;
    const shakeX = Math.sin(this.shakePhase * 13.1) * this.shakeAmount;
    const shakeY = Math.cos(this.shakePhase * 17.7) * this.shakeAmount;
    return {
      x: c.x + (point.x - this.x) * this.zoom + shakeX,
      y: c.y + (point.y - this.y) * this.zoom + shakeY,
    };
  }

  screenToWorld(point: Point): Point {
    const c = this.focusCenter;
    return {
      x: (point.x - c.x) / this.zoom + this.x,
      y: (point.y - c.y) / this.zoom + this.y,
    };
  }

  /** Hex centre in screen pixels. */
  hexToScreen(coord: Axial): Point {
    return this.worldToScreen(toPixel(coord, HEX_SIZE));
  }

  hexKeyToScreen(id: HexKey): Point {
    return this.hexToScreen(parseKey(id));
  }

  /** Which hex a screen point falls in — used for mouse/touch selection. */
  screenToHex(point: Point): Axial {
    return fromPixel(this.screenToWorld(point), HEX_SIZE);
  }

  /** Hex corner points in screen space, for drawing plates. */
  hexCorners(coord: Axial, sizeScale = 1): Point[] {
    return corners(this.hexToScreen(coord), HEX_SIZE * this.zoom * sizeScale);
  }

  /** Rendered hex circumradius in screen pixels. */
  get hexScreenSize(): number {
    return HEX_SIZE * this.zoom;
  }

  /* ------------------------------------------------------------------ control */

  /** Snap the whole board into the visible area with a margin. */
  frameBoard(margin = 0.92): void {
    const { width, height, insets } = this.viewport;
    const usableW = Math.max(120, width - insets.left - insets.right);
    const usableH = Math.max(120, height - insets.top - insets.bottom);

    // The board's world extent: a radius-N pointy-top hex field spans
    // ±(N + 0.5)·√3·size horizontally and ±(1.5N + 1)·size vertically.
    const worldW = (MAP_RADIUS + 0.6) * Math.sqrt(3) * HEX_SIZE * 2;
    const worldH = (MAP_RADIUS * 1.5 + 1.1) * HEX_SIZE * 2;

    const fit = Math.min(usableW / worldW, usableH / worldH) * margin;
    this.targetZoom = clamp(fit, MIN_ZOOM, MAX_ZOOM);
    this.targetX = 0;
    this.targetY = 0;
  }

  /** Immediately match the eased target — used on first frame and after a resize. */
  snapToTarget(): void {
    this.x = this.targetX;
    this.y = this.targetY;
    this.zoom = this.targetZoom;
  }

  panBy(dxWorld: number, dyWorld: number): void {
    this.targetX = clamp(this.targetX + dxWorld, -this.panLimitX, this.panLimitX);
    this.targetY = clamp(this.targetY + dyWorld, -this.panLimitY, this.panLimitY);
  }

  zoomBy(factor: number): void {
    this.targetZoom = clamp(this.targetZoom * factor, MIN_ZOOM, MAX_ZOOM);
    // Zooming out past the fit means empty space on all sides, so pull the centre back.
    this.targetX = clamp(this.targetX, -this.panLimitX, this.panLimitX);
    this.targetY = clamp(this.targetY, -this.panLimitY, this.panLimitY);
  }

  setZoom(zoom: number): void {
    this.targetZoom = clamp(zoom, MIN_ZOOM, MAX_ZOOM);
  }

  /**
   * Keep a hex comfortably inside the usable area.
   *
   * Rather than hard-centring on the cursor (which makes the whole board slide under the
   * player and is nauseating), this only nudges when the target drifts outside a central
   * dead zone.
   */
  keepInView(coord: Axial, deadZone = 0.62): void {
    const screen = this.hexToScreen(coord);
    const { width, height, insets } = this.viewport;
    const usableW = width - insets.left - insets.right;
    const usableH = height - insets.top - insets.bottom;
    const c = this.focusCenter;

    const limitX = (usableW * deadZone) / 2;
    const limitY = (usableH * deadZone) / 2;
    const overX = Math.abs(screen.x - c.x) - limitX;
    const overY = Math.abs(screen.y - c.y) - limitY;

    if (overX > 0) {
      this.targetX += Math.sign(screen.x - c.x) * (overX / this.zoom);
    }
    if (overY > 0) {
      this.targetY += Math.sign(screen.y - c.y) * (overY / this.zoom);
    }
    this.targetX = clamp(this.targetX, -this.panLimitX, this.panLimitX);
    this.targetY = clamp(this.targetY, -this.panLimitY, this.panLimitY);
  }

  /** Centre on a hex, for "jump to my next fleet". */
  focusOn(coord: Axial): void {
    const world = toPixel(coord, HEX_SIZE);
    this.targetX = clamp(world.x, -this.panLimitX, this.panLimitX);
    this.targetY = clamp(world.y, -this.panLimitY, this.panLimitY);
  }

  shake(amount = 7): void {
    this.shakeAmount = Math.max(this.shakeAmount, amount);
  }

  /**
   * Ease toward the target. Frame-rate independent: the smoothing constant is converted
   * into a per-millisecond decay so a 30 Hz display and a 144 Hz display feel the same.
   */
  update(dtMs: number): void {
    const dt = Math.min(dtMs, 100);
    const k = 1 - Math.exp(-dt / 90);

    this.x += (this.targetX - this.x) * k;
    this.y += (this.targetY - this.y) * k;
    this.zoom += (this.targetZoom - this.zoom) * k;

    this.shakePhase += dt / 16.67;
    this.shakeAmount *= Math.exp(-dt / 70);
    if (this.shakeAmount < 0.05) this.shakeAmount = 0;
  }

  resize(width: number, height: number, dpr: number, insets?: Partial<Viewport['insets']>): void {
    this.viewport = {
      width,
      height,
      dpr,
      insets: { ...this.viewport.insets, ...insets },
    };
    this.frameBoard();
  }

  setInsets(insets: Partial<Viewport['insets']>): void {
    this.viewport.insets = { ...this.viewport.insets, ...insets };
  }

  /**
   * How far the centre may travel from the origin. Scales with zoom so that at the
   * fitted zoom there is nothing to pan (the board already fills the view), and at high
   * zoom you can reach every edge.
   */
  private get panLimitX(): number {
    const worldHalf = (MAP_RADIUS + 0.6) * Math.sqrt(3) * HEX_SIZE;
    const visibleHalf = (this.viewport.width * 0.5) / Math.max(this.zoom, 0.01);
    return Math.max(0, worldHalf - visibleHalf * 0.6);
  }

  private get panLimitY(): number {
    const worldHalf = (MAP_RADIUS * 1.5 + 1.1) * HEX_SIZE;
    const visibleHalf = (this.viewport.height * 0.5) / Math.max(this.zoom, 0.01);
    return Math.max(0, worldHalf - visibleHalf * 0.6);
  }

  /** Is this hex worth drawing? Cheap frustum cull for the detail passes. */
  isVisible(coord: Axial, pad = 1.6): boolean {
    const p = this.hexToScreen(coord);
    const r = this.hexScreenSize * pad;
    return (
      p.x + r >= 0 && p.x - r <= this.viewport.width &&
      p.y + r >= 0 && p.y - r <= this.viewport.height
    );
  }
}

function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}
